import React from 'react';
import {
  FormControl,
  FormLabel,
  TextField,
  Grid,
  Select,
  MenuItem,
} from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { ErrorText } from '../../../../../theme/components/Forms';
import ISO3316Util from '../../../../../util/ISO3316Util';
import { FormikProps } from 'formik';

interface AddressFields {
  street: string;
  zipCode: string;
  city: string;
  country: string;
}

interface AddressFormFieldsProps<T extends AddressFields> {
  formik: FormikProps<T>;
}

const AddressFormFields = <T extends AddressFields>({
  formik,
}: AddressFormFieldsProps<T>) => {
  const polyglot = getPolyglot();

  return (
    <>
      {/* Street Address */}
      <FormControl
        fullWidth
        sx={{
          gap: '0.5rem',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <FormLabel htmlFor="street" required>
          {polyglot.t('group.location.street')}
        </FormLabel>
        <TextField
          fullWidth
          id="street"
          name="street"
          type="text"
          variant="outlined"
          autoComplete="street-address"
          value={formik.values.street}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          error={formik.touched.street && Boolean(formik.errors.street)}
          helperText={
            formik.touched.street &&
            formik.errors.street && (
              <ErrorText>
                {polyglot.t(formik.errors.street as string)}
              </ErrorText>
            )
          }
        />
      </FormControl>

      {/* Zip Code and City */}
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} p={0}>
          <FormControl
            fullWidth
            sx={{
              gap: '0.5rem',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <FormLabel htmlFor="zipCode" required>
              {polyglot.t('group.location.zip_code')}
            </FormLabel>
            <TextField
              fullWidth
              id="zipCode"
              name="zipCode"
              type="text"
              variant="outlined"
              autoComplete="postal-code"
              value={formik.values.zipCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
              helperText={
                formik.touched.zipCode &&
                formik.errors.zipCode && (
                  <ErrorText>
                    {polyglot.t(formik.errors.zipCode as string)}
                  </ErrorText>
                )
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormControl
            fullWidth
            sx={{
              gap: '0.5rem',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <FormLabel htmlFor="city" required>
              {polyglot.t('group.location.city')}
            </FormLabel>
            <TextField
              fullWidth
              id="city"
              name="city"
              type="text"
              variant="outlined"
              autoComplete="address-level2"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={
                formik.touched.city &&
                formik.errors.city && (
                  <ErrorText>
                    {polyglot.t(formik.errors.city as string)}
                  </ErrorText>
                )
              }
            />
          </FormControl>
        </Grid>
      </Grid>

      {/* Country */}
      <FormControl
        fullWidth
        sx={{
          gap: '0.5rem',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
        required
        error={formik.touched.country && Boolean(formik.errors.country)}
      >
        <FormLabel htmlFor="country" required>
          {polyglot.t('group.location.country')}
        </FormLabel>
        <Select
          id="country"
          name="country"
          value={formik.values.country}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          displayEmpty
        >
          <MenuItem value="" disabled>
            {polyglot.t('group.location.select_country')}
          </MenuItem>
          {ISO3316Util.getAllCountries(polyglot.locale()).map((country) => (
            <MenuItem key={country.code} value={country.code}>
              {country.name}
            </MenuItem>
          ))}
        </Select>
        {formik.touched.country && formik.errors.country && (
          <ErrorText>{polyglot.t(formik.errors.country as string)}</ErrorText>
        )}
      </FormControl>
    </>
  );
};

export default AddressFormFields;
